import { useAnalyticsEmitter } from '@utils/analytics/emitter';
import { cn } from '@utils/cn';
import Link from 'next/link';
import React, { useEffect, useRef, useState } from 'react';

import type { Tab } from './Tabs';
import styles from './verticalTabs.module.css';

export type VerticalTab = Tab & {
  tabs?: Tab[];
};

type Marker = {
  top: number;
  height: number;
};

let lastMarker: Marker | null = null;

type Props = {
  'aria-label'?: string;
  onClick?: (key: string) => void;
  activeTab: string;
  tabs: VerticalTab[];
};

export const VerticalTabs = ({
  activeTab,
  'aria-label': ariaLabel,
  onClick,
  tabs,
}: Props) => {
  const [emitter] = useAnalyticsEmitter();
  const activeRef = useRef<HTMLAnchorElement>(null);
  const [marker, setMarker] = useState(lastMarker);

  useEffect(() => {
    const link = activeRef.current;
    const next = link
      ? { top: link.offsetTop, height: link.offsetHeight }
      : null;

    lastMarker = next;

    const frame = requestAnimationFrame(() => {
      setMarker(next);
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [activeTab]);

  const renderTab = (tab: Tab, isSubTab: boolean) => {
    return (
      <Link
        ref={tab.key === activeTab ? activeRef : undefined}
        aria-current={tab.key === activeTab ? 'page' : undefined}
        as={tab.hrefAs}
        className={cn(
          styles.UiVerticalTabs__tab,
          isSubTab && styles['UiVerticalTabs__tab--sub'],
        )}
        href={tab.href}
        onClick={() => {
          emitter('', 'tab clicked', {
            'current tab': activeTab,
            'target tab': tab.key,
          });

          onClick?.(tab.key);
        }}
      >
        {tab.label}
      </Link>
    );
  };

  return (
    <nav
      aria-label={ariaLabel}
      className={styles.UiVerticalTabs}
      data-sliding={marker !== null}
    >
      <ul className={styles.UiVerticalTabs__list}>
        {tabs.map((tab) => {
          return (
            <li key={tab.key}>
              {renderTab(tab, false)}
              {tab.tabs?.length ? (
                <ul>
                  {tab.tabs.map((subTab) => {
                    return <li key={subTab.key}>{renderTab(subTab, true)}</li>;
                  })}
                </ul>
              ) : null}
            </li>
          );
        })}
      </ul>
      {marker ? (
        <span
          aria-hidden="true"
          className={styles.UiVerticalTabs__marker}
          style={{
            height: marker.height,
            transform: `translateY(${marker.top}px)`,
          }}
        />
      ) : null}
    </nav>
  );
};
